import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const DEVICE_KEY = 'ef365-license-device-v1';
const RECEIPT_KEY = 'ef365-license-receipt-v1';

export interface LicenseDeviceContext {
  deviceId: string;
  deviceName: string;
  platform: string;
  appVersion: string;
  native: boolean;
}

export interface CachedLicenseReceipt {
  receipt: string;
  publicKeyB64Url: string;
  product: string;
  expiresAt: string;
  offlineGraceUntil: string;
  storedAt: string;
}

interface ReceiptPayload {
  iss?: string;
  sub?: string;
  account_ref?: string;
  product?: string;
  client_id?: string;
  plan?: string;
  status?: string;
  expires_at?: string;
  offline_grace_until?: string;
  device_id?: string;
  issued_at?: string;
}

function decodeBase64Url(value: string) {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(normalized);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function getOrCreateNativeDeviceId() {
  const existing = await Preferences.get({ key: DEVICE_KEY });
  if (existing.value) return existing.value;
  const value = crypto.randomUUID();
  await Preferences.set({ key: DEVICE_KEY, value });
  return value;
}

export async function getLicenseDeviceContext(): Promise<LicenseDeviceContext> {
  const native = Capacitor.isNativePlatform();
  const platform = native ? Capacitor.getPlatform() : (window.location.pathname.startsWith('/telegram') ? 'telegram-mini-app' : 'web');
  // Web/Telegram intentionally do not consume a License Hub device slot. Android does.
  const deviceId = native ? await getOrCreateNativeDeviceId() : '';
  const deviceName = native ? `English Forge ${platform}` : '';
  return {
    deviceId,
    deviceName,
    platform,
    appVersion: String(import.meta.env.VITE_APP_VERSION ?? '3.0.0'),
    native,
  };
}

export async function cacheLicenseReceipt(input: Omit<CachedLicenseReceipt, 'storedAt'>) {
  if (!input.receipt || !input.publicKeyB64Url) return;
  await Preferences.set({ key: RECEIPT_KEY, value: JSON.stringify({ ...input, storedAt: new Date().toISOString() }) });
}

export async function clearCachedLicenseReceipt() {
  await Preferences.remove({ key: RECEIPT_KEY });
}

export async function readCachedLicenseReceipt(): Promise<CachedLicenseReceipt | null> {
  const { value } = await Preferences.get({ key: RECEIPT_KEY });
  if (!value) return null;
  try { return JSON.parse(value) as CachedLicenseReceipt; } catch { return null; }
}

export async function verifyCachedNativeLicense(): Promise<{ active: boolean; accessUntil?: string; payload?: ReceiptPayload; reason?: string }> {
  const device = await getLicenseDeviceContext();
  // Offline entitlement is intentionally a native-only feature. Web/PWA must re-check the server.
  if (!device.native || !device.deviceId) return { active: false, reason: 'OFFLINE_NATIVE_ONLY' };
  const cached = await readCachedLicenseReceipt();
  if (!cached) return { active: false, reason: 'NO_CACHED_RECEIPT' };
  try {
    const [bodyPart, sigPart] = cached.receipt.split('.', 2);
    if (!bodyPart || !sigPart) return { active: false, reason: 'BAD_RECEIPT_FORMAT' };
    const body = decodeBase64Url(bodyPart);
    const signature = decodeBase64Url(sigPart);
    const rawKey = decodeBase64Url(cached.publicKeyB64Url);
    const key = await crypto.subtle.importKey('raw', rawKey, { name: 'Ed25519' }, false, ['verify']);
    const verified = await crypto.subtle.verify({ name: 'Ed25519' }, key, signature, body);
    if (!verified) return { active: false, reason: 'BAD_RECEIPT_SIGNATURE' };
    const payload = JSON.parse(new TextDecoder().decode(body)) as ReceiptPayload;
    if (payload.iss !== 'sysone-license-hub-v2') return { active: false, reason: 'BAD_RECEIPT_ISSUER' };
    if (payload.product !== cached.product) return { active: false, reason: 'WRONG_PRODUCT' };
    if (payload.device_id && payload.device_id !== device.deviceId) return { active: false, reason: 'WRONG_DEVICE' };
    if (payload.status !== 'active') return { active: false, reason: 'RECEIPT_NOT_ACTIVE' };
    const accessUntil = payload.offline_grace_until || payload.expires_at || cached.offlineGraceUntil || cached.expiresAt;
    if (!accessUntil || new Date(accessUntil).getTime() <= Date.now()) return { active: false, reason: 'OFFLINE_GRACE_EXPIRED' };
    return { active: true, accessUntil, payload };
  } catch {
    return { active: false, reason: 'RECEIPT_VERIFY_UNAVAILABLE' };
  }
}
